// ── Enums ──────────────────────────────────────────────────────────────────

export type TaskStatus = "todo_later" | "todo_now" | "in_progress" | "waiting" | "done" | "cancelled" | "n_d";
export type TaskPriority = "low" | "medium" | "high";
export type NotificationType =
  | "assigned"
  | "unassigned"
  | "status_changed"
  | "task_closed"
  | "comment_added"
  | "due_soon"
  | "mentioned"
  | "subtask_done"
  | "kpi_task_ping"
  | "subtask_updated"
  | "task_created"
  | "order_deadline_soon"
  | "order_deadline_today"
  | "order_overdue"
  | "order_comment_new"
  | "leave_approved"
  | "leave_rejected"
  | "remote_work_approved"
  | "remote_work_rejected"
  | "order_updated"
  | "order_access_granted"
  | "order_cancelled"
  | "order_acceptance_warning"
  | "order_deleted"
  | "order_change_request"
  | "order_change_approved"
  | "order_change_rejected"
  | "pock_request_submitted"
  | "pock_request_approved"
  | "pock_request_rejected"
  | "pock_accepted"
  | "pock_rejected"
  | "pock_orderer_acceptance_required"
  | "pock_orderer_accepted"
  | "pock_orderer_rejected"
  | "order_result_file_uploaded"
  | "order_result_file_rejected"
  | "mdcp_photos_uploaded"
  | "mdcp_photos_rejected"
  | "invoice_submitted"
  | "invoice_accepted"
  | "invoice_rejected"
  | "issue_resolved"
  | "issue_wont_fix"
  | "issue_reopened"
  | "issue_comment_added"
  | "resource_version_uploaded"
  | "prz_proforma_paid"
  | "official_fee_submitted"
  | "official_fee_approved"
  | "official_fee_rejected"
  | "official_fee_payment_uploaded"
  | "official_fee_acknowledged"
  | "official_fee_attachment_added"
  | "wypis_ordering_reminder"
  | "ze_report_due_soon"
  | "project_coordinator_assigned"
  | "chat_mentioned"
  | "requisition_note_mentioned"
  | "wpw_awaiting_confirmation"
  | "wpw_problem"
  | "wpw_confirmed"
  | "wpw_rejected"
  | "wpw_attachments_updated"
  | "wpw_mail_auto_dispatched"
  | "wpw_mail_held"
  | "wpw_mail_returned"
  | "parcel_problem"
  | "proceeding_response"
  | "application_approval_requested"
  | "application_approval_rejected"
  | "application_approval_granted"
  | "deliverable_submitted"
  | "deliverable_needs_correction"
  | "deliverable_accepted"
  | "cost_estimate_submitted"
  | "cost_estimate_needs_correction"
  | "cost_estimate_accepted"
  | "correspondence_filed"
  | "correspondence_returned"
  | "employee_access_expired_contract"
  | "supply_item_submitted"
  | "supply_item_approved"
  | "preset_function_needs_approval"
  | "preset_function_approved"
  | "preset_function_rejected"
  | "print_order_submitted"
  | "print_order_accepted"
  | "print_order_rejected"
  | "print_order_returned"
  | "meeting_invited"
  | "meeting_updated"
  | "meeting_cancelled"
  | "meeting_reminder"
  | "meeting_started"
  | "eng_review_shared"
  | "eng_review_decided"
  | "two_drive_shared"
  | "two_drive_item_added"
  | "two_drive_item_removed"
  | "two_drive_item_changed"
  | "lead_reply_received"
  | "notif_delegation_requested"
  | "notif_delegation_accepted"
  | "notif_delegation_declined"
  | "notif_delegation_ended"
  | "sms_account_low_balance"
  // Projekt ZE prowadzony przez podwykonawcę: ruch firmy w „Etapach" → opiekun ELEKTIM.
  | "led_tile_activity"
  // …i w drugą stronę: ruch GK ELEKTIM w „Etapach" → konta firmy prowadzącej.
  | "led_tile_gk_activity";

// ── Labels & colors ────────────────────────────────────────────────────────

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo_later: "Później",
  todo_now: "Do zrobienia",
  in_progress: "W trakcie",
  waiting: "Wstrzymano",
  done: "Ukończone",
  cancelled: "Anulowane",
  n_d: "N/D",
};

export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  todo_later: "bg-gray-100 text-gray-500",
  todo_now: "bg-blue-100 text-blue-700",
  in_progress: "bg-violet-100 text-violet-700",
  waiting: "bg-yellow-100 text-yellow-700",
  done: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-600",
  n_d: "bg-slate-100 text-slate-500",
};

export const TASK_STATUS_BORDER: Record<TaskStatus, string> = {
  todo_later: "border-gray-300 dark:border-gray-500",
  todo_now: "border-blue-400",
  in_progress: "border-violet-400",
  waiting: "border-yellow-400",
  done: "border-green-400",
  cancelled: "border-rose-400",
  n_d: "border-slate-300 dark:border-slate-500",
};

export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: "Niski",
  medium: "Średni",
  high: "Wysoki",
};

export const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: "bg-gray-100 text-gray-600",
  medium: "bg-blue-50 text-blue-600",
  high: "bg-orange-100 text-orange-700",
};

export const PRIORITY_DOT: Record<TaskPriority, string> = {
  low: "bg-gray-400",
  medium: "bg-blue-500",
  high: "bg-orange-500",
};

export const NOTIFICATION_LABELS: Record<NotificationType, string> = {
  assigned: "Przypisano do zadania",
  unassigned: "Usunięto z zadania",
  status_changed: "Zmieniono status",
  task_closed: "Zadanie zamknięte",
  comment_added: "Nowy komentarz",
  due_soon: "Zbliża się termin",
  mentioned: "Wspomniano o Tobie",
  subtask_done: "Podzadanie ukończone",
  kpi_task_ping: "Ponaglenie zadania KPI",
  subtask_updated: "Zmiana w podzadaniu",
  task_created: "Nowe zadanie",
  order_deadline_soon: "Zbliża się termin zamówienia",
  order_deadline_today: "Termin zamówienia dzisiaj",
  order_overdue: "Zamówienie po terminie",
  order_comment_new: "Nowy komentarz w zleceniu",
  leave_approved: "Wniosek urlopowy zatwierdzony",
  leave_rejected: "Wniosek urlopowy odrzucony",
  remote_work_approved: "Wniosek o pracę zdalną zatwierdzony",
  remote_work_rejected: "Wniosek o pracę zdalną odrzucony",
  order_updated: "Zmieniono zamówienie",
  order_access_granted: "Nadano dostęp do zamówienia",
  order_cancelled: "Zamówienie anulowane",
  order_acceptance_warning: "Mija termin przyjęcia zamówienia",
  order_deleted: "Usunięto Twoje zamówienie",
  order_change_request: "Nowy wniosek o zmianę zamówienia",
  order_change_approved: "Wniosek o zmianę zatwierdzony",
  order_change_rejected: "Wniosek o zmianę odrzucony",
  pock_request_submitted: "Nowy wniosek o POCK",
  pock_request_approved: "Wniosek o POCK zatwierdzony",
  pock_request_rejected: "Wniosek o POCK odrzucony",
  pock_accepted: "POCK zaakceptowany przez podwykonawcę",
  pock_rejected: "POCK odrzucony przez podwykonawcę",
  pock_orderer_acceptance_required: "POCK końcowy do akceptacji zamawiającego",
  pock_orderer_accepted: "POCK zaakceptowany przez zamawiającego",
  pock_orderer_rejected: "POCK odrzucony przez zamawiającego",
  order_result_file_uploaded: "Podwykonawca przesłał pliki pozycji realizacyjnej",
  order_result_file_rejected: "Plik pozycji realizacyjnej odrzucony",
  mdcp_photos_uploaded: "Geodeta wgrał zdjęcia MDCP",
  mdcp_photos_rejected: "Zamawiający odrzucił zdjęcia MDCP",
  invoice_submitted: "Nowa faktura P-GK od podwykonawcy",
  invoice_accepted: "Faktura P-GK zaakceptowana",
  invoice_rejected: "Faktura P-GK odrzucona",
  issue_resolved: "Zgłoszenie rozwiązane",
  issue_wont_fix: "Zgłoszenie odrzucone",
  issue_reopened: "Zgłoszenie ponownie otwarte",
  issue_comment_added: "Nowy komentarz w zgłoszeniu",
  resource_version_uploaded: "Nowa wersja dokumentu w repozytorium",
  prz_proforma_paid: "Proforma opłacona — wystaw FV VAT",
  official_fee_submitted: "Nowa prośba o opłatę urzędową",
  official_fee_approved: "Prośba o opłatę urzędową zaakceptowana",
  official_fee_rejected: "Prośba o opłatę urzędową odrzucona",
  official_fee_payment_uploaded: "Potwierdzenie płatności opłaty urzędowej",
  official_fee_acknowledged: "Odbiór potwierdzenia opłaty urzędowej",
  official_fee_attachment_added: "Nowy załącznik do prośby o opłatę urzędową",
  wypis_ordering_reminder: "Wypisy — przypomnienie o zamówieniu",
  ze_report_due_soon: "Zbliża się termin raportu do ZE",
  project_coordinator_assigned: "Przypisano Cię jako koordynatora przedsięwzięcia",
  chat_mentioned: "Wspomniano o Tobie na czacie GG",
  requisition_note_mentioned: "Oznaczono Cię w notatce zapotrzebowania",
  wpw_awaiting_confirmation: "WPW: zgoda do zatwierdzenia przez GK",
  wpw_problem: "WPW: negocjator zgłosił problem",
  wpw_confirmed: "WPW: zatwierdzono i przeniesiono",
  wpw_rejected: "WPW: odrzucono zgłoszenie",
  wpw_attachments_updated: "WPW: GK zaktualizował załączniki zlecenia",
  wpw_mail_auto_dispatched: "WPW: automatycznie zlecono wysyłkę listu",
  wpw_mail_held: "WPW: wysyłka listu wstrzymana",
  wpw_mail_returned: "WPW: wrócił list od właściciela",
  parcel_problem: "Działka: status problem",
  proceeding_response: "Sprawa: wpłynęła odpowiedź urzędu",
  application_approval_requested: "Wniosek urzędowy czeka na akceptację",
  application_approval_rejected: "Wniosek urzędowy zwrócony do poprawy",
  application_approval_granted: "Wniosek urzędowy zatwierdzony i złożony",
  deliverable_submitted: "Opracowanie złożone do sprawdzenia",
  deliverable_needs_correction: "Opracowanie skierowane do poprawy",
  deliverable_accepted: "Opracowanie zaakceptowane",
  cost_estimate_submitted: "Kosztorys złożony do sprawdzenia",
  cost_estimate_needs_correction: "Kosztorys skierowany do poprawy",
  cost_estimate_accepted: "Kosztorys zaakceptowany",
  correspondence_filed: "Korespondencja zadekretowana na Ciebie",
  correspondence_returned: "Korespondencja zwrócona / niedoręczalna",
  employee_access_expired_contract: "Pracownik z wygasłą umową ma nieodebrane dostępy",
  supply_item_submitted: "Nowe zapotrzebowanie biurowe — do akceptacji",
  supply_item_approved: "Zapotrzebowanie biurowe zaakceptowane — do zamówienia",
  preset_function_needs_approval: "Nowa funkcja presetu wymaga zatwierdzenia",
  preset_function_approved: "Funkcja presetu zatwierdzona",
  preset_function_rejected: "Funkcja presetu odrzucona",
  print_order_submitted: "Nowe zlecenie wydruku do akceptacji",
  print_order_accepted: "Zlecenie wydruku do realizacji",
  print_order_rejected: "Zlecenie wydruku odrzucone",
  print_order_returned: "Zwrot zlecenia wydruku",
  meeting_invited: "Zaproszenie na spotkanie",
  meeting_updated: "Zmiana terminu spotkania",
  meeting_cancelled: "Spotkanie odwołane",
  meeting_reminder: "Przypomnienie o spotkaniu",
  meeting_started: "Spotkanie rozpoczęte",
  eng_review_shared: "Recenzja opracowania udostępniona Tobie",
  eng_review_decided: "Decyzja recenzenta o Twoim opracowaniu",
  two_drive_shared: "Udostępniono Ci plik lub folder",
  two_drive_item_added: "Dodano element do udostępnionego folderu",
  two_drive_item_removed: "Usunięto element z udostępnionego folderu",
  two_drive_item_changed: "Zmieniono element w udostępnionym folderze",
  lead_reply_received: "Firma odpisała na mail z kampanii",
  notif_delegation_requested: "Prośba o przejęcie powiadomień",
  notif_delegation_accepted: "Przejęcie powiadomień zaakceptowane",
  notif_delegation_declined: "Przejęcie powiadomień odrzucone",
  notif_delegation_ended: "Przekazywanie powiadomień zakończone",
  sms_account_low_balance: "Konto SMSAPI bez środków — SMS-y czekają w kolejce",
  led_tile_activity: "Etapy: zmiana od firmy prowadzącej projekt",
  led_tile_gk_activity: "Etapy: zmiana od GK ELEKTIM",
};

/**
 * Co się wydarzyło. Typ dodany tylko na backendzie (brak w NOTIFICATION_LABELS)
 * dostaje etykietę z backendu — inaczej wiersz dzwonka zostaje bez treści.
 */
export function notificationLabel(n: Pick<TaskNotification, "notification_type" | "type_label">): string {
  return NOTIFICATION_LABELS[n.notification_type] ?? n.type_label ?? "";
}

export const TASK_STATUS_ORDER: TaskStatus[] = [
  "todo_later",
  "todo_now",
  "in_progress",
  "waiting",
  "done",
  "cancelled",
  "n_d",
];

// ── Domain types ───────────────────────────────────────────────────────────

export interface TaskCategory {
  id: string;
  name: string;
  color: string;
  created_at: string;
}

export interface TaskAssignee {
  id: string;
  full_name: string;
  role: string;
  profile_picture_url: string | null;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  project: string | null;
  project_name: string | null;
  created_by: string | null;
  created_by_name: string | null;
  assignees: TaskAssignee[];
  subtask_count: number;
  done_subtask_count: number;
  comments_count: number;
  last_comment_at: string | null;
  is_watching: boolean;
  due_date: string | null;
  position: number;
  is_expanded: boolean;
  is_pinned: boolean;
  my_category: { id: string; name: string; color: string } | null;
  is_archived: boolean;
  can_archive: boolean;
  created_at: string;
  updated_at: string;
}

export interface TaskDetail extends Task {
  subtasks: SubTask[];
  comments: TaskComment[];
}

export interface TaskFormData {
  title: string;
  description?: string;
  project?: string | null;
  assignee_ids?: string[];
  position?: number;
  due_date?: string | null;
}

export interface SubTask {
  id: string;
  task: string;
  task_title: string;
  task_project_name: string | null;
  parent: string | null;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignees: TaskAssignee[];
  due_date: string | null;
  weeks_until_due: number | null;
  position: number;
  comments_count: number;
  children_count: number;
  is_watching: boolean;
  done_by_cascade: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubTaskFilters {
  status?: TaskStatus;
  priority?: TaskPriority;
  project?: string[];
  client?: string[];
  assigned_to?: string[];
  due_from?: string;
  due_to?: string;
  search?: string;
  include_archived?: boolean;
}

export interface SubTaskAttachment {
  id: string;
  original_name: string;
  url: string;
  uploaded_by_name: string | null;
  created_at: string;
}

export interface SubTaskCommentAttachment {
  id: string;
  original_name: string;
  url: string;
  created_at: string;
}

export interface CommentMention {
  id: string;
  full_name: string;
}

export interface SubTaskComment {
  id: string;
  subtask: string;
  author: string | null;
  author_name: string;
  author_role: string;
  author_is_ceo?: boolean;
  author_profile_picture_url: string | null;
  reply_to: string | null;
  reply_to_author_name: string | null;
  reply_to_content: string | null;
  content: string;
  is_edited: boolean;
  attachments: SubTaskCommentAttachment[];
  mentions: CommentMention[];
  created_at: string;
  updated_at: string;
}

export interface TaskComment {
  id: string;
  task: string;
  author: string | null;
  author_name: string;
  author_role: string;
  author_is_ceo?: boolean;
  author_profile_picture_url: string | null;
  reply_to: string | null;
  reply_to_author_name: string | null;
  reply_to_content: string | null;
  content: string;
  is_edited: boolean;
  mentions: CommentMention[];
  created_at: string;
  updated_at: string;
}

export interface TaskNotification {
  id: string;
  task: string | null;
  title: string;
  url: string;
  entity_type: string;
  notification_type: NotificationType;
  /** Etykieta typu z backendu (choices) — fallback dla typów spoza NOTIFICATION_LABELS. */
  type_label?: string;
  is_read: boolean;
  is_pinned: boolean;
  actor: string | null;
  actor_name: string | null;
  extra_data: Record<string, unknown>;
  created_at: string;
  // Powiadomienia dotyczące tego samego miejsca są scalane w jedną pozycję:
  // group_count to liczba zdarzeń, group_latest_at czas najnowszego z nich.
  group_count?: number;
  group_ids?: string[];
  group_latest_at?: string;
  // Zakładka dzwonka — backend liczy ją z całej scalonej pozycji
  // (apps/tasks/notification_categories.py), front tylko filtruje.
  category?: NotificationCategory;
}

// ── Zakładki dzwonka ─────────────────────────────────────────────────────────
// Kolejność = kolejność zakładek w panelu i zarazem ważność przy scalaniu.

export type NotificationCategory = "me" | "approval" | "deadline" | "system" | "other";

export type NotificationTab = "all" | NotificationCategory;

export const NOTIFICATION_CATEGORY_ORDER: NotificationCategory[] = [
  "me",
  "approval",
  "deadline",
  "system",
  "other",
];

export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  me: "Dla mnie",
  approval: "Zatwierdzanie",
  deadline: "Terminy",
  system: "System",
  other: "Inne",
};

export interface WeeklyPlan {
  id: string;
  week_start: string;
  week_label: string;
  project: string | null;
  project_name: string | null;
  notes: string;
  tasks: Task[];
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface WeeklyPlanFormData {
  week_start: string;
  project?: string | null;
  notes?: string;
  task_ids?: string[];
}

export interface TaskFilters {
  project?: string[];
  client?: string[];
  assigned_to?: string[];
  search?: string;
  category?: string;
  no_category?: boolean;
  include_archived?: boolean;
}

// Employee task statistics — director panel
export interface EmployeeTaskStats {
  user_id: string;
  full_name: string;
  role: string;
  is_ceo?: boolean;
  profile_picture_url: string | null;
  done: number;
  todo_now: number;
  todo_later: number;
  in_progress: number;
  waiting: number;
  overdue: number;
  total_active: number;
}

export interface SpreadsheetLink {
  id: string;
  name: string;
  embed_code: string;
  created_by_name: string | null;
  created_at: string;
}

// Internal user for assignment picker
export interface InternalUser {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  is_ceo?: boolean;
  is_developer?: boolean;
  has_friday_access?: boolean;
  contractor_company_name?: string | null;
  profile_picture_url: string | null;
}
